import type { TeamLocation } from '@/components/events/live/useTeamLocations';

/**
 * A team is flagged as split beyond this distance.
 *
 * Roughly a few city blocks: far enough that the group genuinely isn't
 * together, loose enough that a team spread across a plaza or queuing
 * outside a venue isn't flagged. Advisory only — nothing is blocked, the
 * facilitator is just told.
 */
export const SPLIT_TEAM_THRESHOLD_METERS = 200;

/** Teams whose members have drifted far enough apart to be worth flagging. */
export function splitTeams(teams: readonly TeamLocation[]): TeamLocation[] {
  return teams.filter(
    (team) => team.spreadMeters !== null && team.spreadMeters > SPLIT_TEAM_THRESHOLD_METERS,
  );
}

/**
 * Age of a fix in words.
 *
 * `now` is injected rather than read from the clock so this is testable
 * and so a list of rows renders against one consistent instant.
 */
export function formatAge(updatedAt: string | null, now: number): string {
  if (!updatedAt) return 'no signal';
  const parsed = new Date(updatedAt).getTime();
  if (Number.isNaN(parsed)) return 'no signal';
  // Clamped: a device a few seconds ahead of the server should read as
  // "just now", never as a negative age.
  const seconds = Math.max(0, Math.round((now - parsed) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.round(minutes / 60)}h ago`;
}

export function formatDistance(meters: number): string {
  return meters < 1000 ? `${Math.round(meters)}m` : `${(meters / 1000).toFixed(1)}km`;
}

/** Sentence naming which teams have split, for the advisory banner. */
export function describeSplitTeams(split: readonly TeamLocation[]): string {
  if (split.length === 0) return '';
  const detail = split
    .map((team) => `${team.name} ${formatDistance(team.spreadMeters!)} apart`)
    .join(', ');
  const heading = split.length === 1 ? `${split[0]!.name} has split up` : `${split.length} teams have split up`;
  return `${heading} — ${detail}`;
}
